import React, { useState, useEffect } from "react";
import { Card, Placeholder, Table } from "react-bootstrap";
import { FiEdit2, FiSlash, FiCheckCircle } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import { useFinanceAllocationRule } from "@features/admin/finance/allocation-rule/hooks/useFinanceAllocationRule";
import { useFinanceAllocationRuleOptions } from "@features/admin/finance/allocation-rule/hooks/useFinanceAllocationRuleOptions";
import { useFinanceAllocationRuleAction } from "@features/admin/finance/allocation-rule/hooks/useFinanceAllocationRuleAction";
import { FinanceAllocationRule } from "@features/admin/finance/allocation-rule/types/financeAllocationRule";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import {
    NAME_SORT_FILTER_FIELD,
    SORT_BY_RULE_NAME,
    SORT_DIR_ASC,
    countContentFilters,
    normalizeSortDir,
    readSortDirFromSearchParams,
    withCatalogSort,
} from "@core/constants/listSort";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { useDebounce } from "@core/hooks/useDebounce";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import StatusPill from "@core/components/StatusPill";
import EntityCard from "@core/components/EntityCard";
import NoResult from "@core/components/NoResult";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useEntityContext, usePermissions } from "@features/entity/hooks/usePermissions";
import { ACTION_CODES } from "@features/entity/types/actionCodes";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from "@core/types/errorResponse";
import StatusChangeConfirmModal, { StatusChangeKind } from "@features/admin/finance/components/StatusChangeConfirmModal";
import UpdateAllocationRuleModal from "./UpdateAllocationRuleModal";

const RULE_PERMISSION_CODES = [
    ACTION_CODES.UPDATE_FINANCE_ALLOCATION_RULE,
    ACTION_CODES.DISABLE_FINANCE_ALLOCATION_RULE,
    ACTION_CODES.ENABLE_FINANCE_ALLOCATION_RULE,
];

type StatusConfirmState = {
    rule: FinanceAllocationRule;
    kind: StatusChangeKind;
};

const AllocationRuleTable: React.FC<{ showPagination?: boolean; refreshSignal?: number }> = ({ showPagination = false, refreshSignal }) => {
    const [searchParams] = useSearchParams();
    const resolvedNationId = useScopeId() ?? 0;
    const { context } = useEntityContext();

    const [page, setPage] = useState(() => Number(searchParams.get("page") || "1"));
    const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
    const debouncedSearch = useDebounce(search, 300);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
        const result: Record<string, any> = {};
        searchParams.forEach((value, key) => {
            if (key !== "q" && key !== "page") result[key] = value;
        });
        result.sortDir = readSortDirFromSearchParams(searchParams);
        return result;
    });

    const [selectedRule, setSelectedRule] = useState<FinanceAllocationRule | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [statusConfirm, setStatusConfirm] = useState<StatusConfirmState | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);

    const showError = useApiError();
    const { doDisable, doEnable } = useFinanceAllocationRuleAction();

    const { hasPermission } = usePermissions({
        scopeType: context?.scope,
        scopeId: context?.scopeId,
        actionCodes: RULE_PERMISSION_CODES,
    });

    const canEdit = hasPermission(ACTION_CODES.UPDATE_FINANCE_ALLOCATION_RULE);
    const canDisable = hasPermission(ACTION_CODES.DISABLE_FINANCE_ALLOCATION_RULE);
    const canEnable = hasPermission(ACTION_CODES.ENABLE_FINANCE_ALLOCATION_RULE);

    const { templateKpis, fetchTemplateKpis } = useFinanceAllocationRuleOptions();

    useEffect(() => { fetchTemplateKpis(); }, [fetchTemplateKpis]);

    const RULE_FILTER_FIELDS: FilterField[] = [
        NAME_SORT_FILTER_FIELD,
        {
            name: "isActive",
            label: "Status",
            type: "select",
            options: [
                { value: "1", label: "Active" },
                { value: "0", label: "Inactive" },
            ],
        },
        {
            name: "kpiId",
            label: "KPI",
            type: "select",
            options: templateKpis.map((k) => ({ value: k.kpi_id, label: k.kpi_name })),
        },
    ];

    useSearchUrlSync({ query: debouncedSearch, filters: activeFilters, page });

    const isActiveFilter =
        activeFilters.isActive === "1" ? true :
        activeFilters.isActive === "0" ? false :
        undefined;

    const kpiIdFilter = activeFilters.kpiId ? Number(activeFilters.kpiId) : undefined;

    const { current_page, data, from, last_page, per_page, total, loading, reload } = useFinanceAllocationRule(
        withCatalogSort(
            {
                nationId: resolvedNationId,
                page,
                ...(debouncedSearch ? { freetext: debouncedSearch } : {}),
                ...(isActiveFilter !== undefined ? { isActive: isActiveFilter } : {}),
                ...(kpiIdFilter ? { kpiId: kpiIdFilter } : {}),
                sortDir: normalizeSortDir(activeFilters.sortDir),
            },
            SORT_BY_RULE_NAME
        )
    );

    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({ from, page: pageNumber, perPage: per_page, total, currentCount: data.length });

    // Reload when a sibling create succeeds (skip the initial mount).
    const didMountRef = React.useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    useEffect(() => {
        if (selectedRule) setShowEditModal(true);
    }, [selectedRule]);

    useEffect(() => { setPage(1); }, [debouncedSearch]);

    const openEdit = (rule: FinanceAllocationRule) => {
        setSelectedRule(rule);
    };

    const openStatusConfirm = (rule: FinanceAllocationRule, kind: StatusChangeKind) => {
        setStatusConfirm({ rule, kind });
    };

    const closeStatusConfirm = () => {
        if (!statusBusy) setStatusConfirm(null);
    };

    const handleStatusConfirm = async () => {
        if (!statusConfirm) return;
        const { rule, kind } = statusConfirm;
        setStatusBusy(true);
        try {
            const response = kind === "disable"
                ? await doDisable(resolvedNationId, rule.rule_group_id)
                : await doEnable(resolvedNationId, rule.rule_group_id);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "");
                setStatusConfirm(null);
                await reload();
            } else {
                showError(response.getError() as ErrorResponse);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        } finally {
            setStatusBusy(false);
        }
    };

    // Shared by the desktop table row and the mobile card.
    const renderRuleActions = (r: (typeof data)[number]) => (
        <TableActions aria-label={`Actions for ${r.rule_name}`}>
            {canEdit && (
                <TableActionButton
                    icon={<FiEdit2 />}
                    label="Edit"
                    primary
                    onClick={() => openEdit(r)}
                    data-testid={`rule-edit-${r.rule_group_id}`}
                />
            )}
            {r.is_active && canDisable && (
                <TableActionButton
                    icon={<FiSlash />}
                    label="Disable"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--danger"
                    onClick={() => openStatusConfirm(r, "disable")}
                    data-testid={`rule-disable-${r.rule_group_id}`}
                />
            )}
            {!r.is_active && canEnable && (
                <TableActionButton
                    icon={<FiCheckCircle />}
                    label="Enable"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--success"
                    onClick={() => openStatusConfirm(r, "enable")}
                    data-testid={`rule-enable-${r.rule_group_id}`}
                />
            )}
        </TableActions>
    );

    return (
        <div data-testid="allocation-rule-table">
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder="Search allocation rules…"
                    filterFields={RULE_FILTER_FIELDS}
                    initialQuery={search}
                    initialFilters={activeFilters}
                    onQueryChange={(q) => { setSearch(q); setPage(1); }}
                    onFiltersApply={(f) => {
                        setActiveFilters({ ...f, sortDir: normalizeSortDir(f.sortDir) });
                        setPage(1);
                    }}
                    onClear={() => {
                        setSearch("");
                        setActiveFilters({ sortDir: SORT_DIR_ASC });
                        setPage(1);
                    }}
                    loading={loading}
                    activeFilterCount={countContentFilters(activeFilters)}
                    testId="allocation-rule-search"
                />
            </div>

            <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="allocation-rule-table-card">
                <Card.Body className="p-0">
                    <div className="px-3 pb-3 d-none d-md-block">
                        <Table responsive hover size="sm" className="mb-0 align-middle table-striped ntcog-table">
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th className="d-none d-md-table-cell">#</th>
                                    <th>Rule Name</th>
                                    <th className="d-none d-md-table-cell">KPI</th>
                                    <th>Status</th>
                                    <th className="text-end" style={{ width: 120 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody
                                className="table-group-divider"
                                style={{ opacity: loading && data.length > 0 ? 0.5 : 1, transition: "opacity 0.2s ease" }}
                            >
                                {loading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={`ph-${i}`}>
                                        <td className="d-none d-md-table-cell"><Placeholder xs={3} /></td>
                                        <td><Placeholder xs={6} /></td>
                                        <td className="d-none d-md-table-cell"><Placeholder xs={5} /></td>
                                        <td><Placeholder xs={4} /></td>
                                        <td className="text-end"><Placeholder xs={3} /></td>
                                    </tr>
                                ))}
                                {!loading && data.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="py-5">
                                            <NoResult
                                                line_1="No allocation rules found."
                                                line_2="Try adjusting filters or create a new rule."
                                            />
                                        </td>
                                    </tr>
                                )}
                                {data.length > 0 && data.map((r, i) => {
                                    const rowNumber = from != null ? from + i : i + 1;

                                    return (
                                        <tr key={r.rule_group_id} data-testid={`rule-row-${r.rule_group_id}`} className="bg-white">
                                            <td className="text-muted small d-none d-md-table-cell">{rowNumber}</td>
                                            <td className="fw-semibold">{r.rule_name}</td>
                                            <td className="small text-muted d-none d-md-table-cell">{r.kpi_name_snapshot ?? "—"}</td>
                                            <td>
                                                <StatusPill active={r.is_active} />
                                            </td>
                                            <td className="text-end">
                                                {renderRuleActions(r)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile (< md): stacked cards */}
                    <div className="ntcog-card-list d-md-none" data-testid="allocation-rule-cards">
                        {loading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                            <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`rule-card-skeleton-${i}`}
                                    title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                    description={<Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>}
                                />
                            </div>
                        ))}
                        {!loading && data.length === 0 && (
                            <NoResult
                                line_1="No allocation rules found."
                                line_2="Try adjusting filters or create a new rule."
                            />
                        )}
                        {data.length > 0 && data.map((r) => (
                            <div key={r.rule_group_id} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`rule-card-${r.rule_group_id}`}
                                    ariaLabel={`Allocation rule ${r.rule_name}`}
                                    title={<span className="fw-semibold">{r.rule_name}</span>}
                                    status={<StatusPill active={r.is_active} />}
                                    actions={renderRuleActions(r)}
                                    details={[{ label: "KPI", value: r.kpi_name_snapshot ?? "—" }]}
                                />
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>

            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="allocation-rule-pagination"
                    info={`Showing ${start} to ${end} of ${total} rules`}
                >
                    <PaginationBar
                        page={pageNumber}
                        totalPages={last_page ?? totalPages}
                        onPage={(p) => setPage(p)}
                    />
                </TablePaginationFooter>
            )}

            <UpdateAllocationRuleModal
                show={showEditModal}
                onHide={() => { setShowEditModal(false); setSelectedRule(null); }}
                rule={selectedRule}
                onSuccess={reload}
            />

            <StatusChangeConfirmModal
                show={!!statusConfirm}
                kind={statusConfirm?.kind ?? "disable"}
                entityLabel="rule"
                entityName={statusConfirm?.rule.rule_name ?? ""}
                onConfirm={handleStatusConfirm}
                onCancel={closeStatusConfirm}
                busy={statusBusy}
            />
        </div>
    );
};

export default AllocationRuleTable;
